import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useGetBusinessTypeListQuery } from '@/redux/services/settings/business-types'
import { useFormik } from 'formik'
import { Camera } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as yup from 'yup'
import ModalContainer from '../../../../../components/modals/modal-container'

interface Category {
  id: string
  name: string
  description?: string
  image_url?: string
  position?: number
  is_published: boolean
  active?: boolean
  vendor_count?: number
  business_type_id?: number
  business_type_name?: string
}

interface AddCategoryModalProps {
  show: boolean
  handleClose: () => void
  onSubmit?: (data: CategoryFormData) => void
  loading?: boolean
  editingItem?: Category | null
  preselectedBusinessTypeId?: number
}
interface CategoryFormData {
  name: string
  image?: File
  business_type_id?: number
  position?: number
  active?: boolean
  is_published?: boolean
}

const createCategorySchema = (isEdit: boolean) =>
  yup.object().shape({
    name: yup
      .string()
      .min(2, 'Sub-category name must be at least 2 characters')
      .max(50, 'Sub-category name must be less than 50 characters')
      .required('Sub-category name is required'),
    business_type_id: isEdit
      ? yup.number().optional()
      : yup.number().required('Business type is required'),
    image: isEdit
      ? yup.mixed().optional()
      : yup
            .mixed()
            .required('Image is required')
            .test('fileSize', 'Image size must be less than 1MB', (value) => {
              if (!value) return false
              if (value instanceof File) {
                return value.size <= 1024 * 1024 // 1MB
              }
              return true
            })
            .test('fileType', 'Only .jpg and .png files are allowed', (value) => {
              if (!value) return false
              if (value instanceof File) {
              return ['image/jpeg', 'image/jpg', 'image/png'].includes(
                value.type
              )
            }
            return true
          }),
    active: yup.boolean().optional(),
    is_published: yup.boolean().optional(),
  })

const AddCategoryModal = ({
  show,
  handleClose,
  onSubmit,
  loading,
  editingItem,
  preselectedBusinessTypeId,
}: AddCategoryModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedImageRef = useRef<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(
    editingItem?.image_url || null
  )
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Fetch business types for the dropdown
  const { data: businessTypesData } = useGetBusinessTypeListQuery(
    {
      page: 1,
      per_page: 100,
    },
    {
      skip: !show,
    }
  )

  const businessTypes = businessTypesData?.business_types || []

  const {
    values,
    handleChange,
    touched,
    errors,
    isValid,
    resetForm,
    setFieldTouched,
    setValues,
    setFieldValue,
  } = useFormik({
    initialValues: {
      name: editingItem?.name || '',
      image: undefined as File | undefined,
      business_type_id: editingItem?.business_type_id
        ? Number(editingItem.business_type_id)
        : preselectedBusinessTypeId
        ? Number(preselectedBusinessTypeId)
        : undefined,
      position: editingItem?.position ?? undefined,
      active: editingItem?.active ?? true,
      is_published: editingItem?.is_published ?? false,
    },
    validationSchema: createCategorySchema(!!editingItem),
    onSubmit: (values) => {
      onSubmit?.(values)
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    if (hasSubmitted && loading) {
      return
    }

    if (editingItem) {
      setValues({
        name: editingItem.name,
        image: undefined,
        business_type_id: editingItem.business_type_id
          ? Number(editingItem.business_type_id)
          : undefined,
        position: editingItem.position ?? undefined,
        active: editingItem.active ?? true,
        is_published: editingItem.is_published ?? false,
      })
      setImagePreview(editingItem.image_url || null)
      if (!hasSubmitted) {
        setSelectedImage(null)
        selectedImageRef.current = null
      }
    } else {
      setValues({
        name: '',
        image: undefined,
        business_type_id: preselectedBusinessTypeId
          ? Number(preselectedBusinessTypeId)
          : undefined,
        position: undefined,
        active: true,
        is_published: false,
      })
      setImagePreview(null)
      if (!hasSubmitted) {
        setSelectedImage(null)
        selectedImageRef.current = null
      }
    }
    if (!hasSubmitted) {
      setHasSubmitted(false)
    }
  }, [editingItem, setValues, hasSubmitted, loading, preselectedBusinessTypeId])

  useEffect(() => {
    if (hasSubmitted && !loading && !show) {
      resetForm()
      if (!editingItem) {
        setImagePreview(null)
      } else {
        setImagePreview(editingItem?.image_url || null)
      }
      setSelectedImage(null)
      selectedImageRef.current = null
      setHasSubmitted(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [hasSubmitted, loading, show, editingItem, resetForm])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png']
      if (!validTypes.includes(file.type)) {
        toast.error('Only .jpg and .png files are allowed')
        return
      }
      // Validate file size (1MB)
      if (file.size > 1024 * 1024) {
        toast.error('Image size must be less than 1MB')
        return
      }
      setSelectedImage(file)
      selectedImageRef.current = file
      setFieldValue('image', file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCloseModal = () => {
    resetForm()
    setImagePreview(editingItem?.image_url || null)
    setSelectedImage(null)
    selectedImageRef.current = null
    setHasSubmitted(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    handleClose()
  }

  return (
    <ModalContainer
      show={show}
      handleClose={handleCloseModal}
      modalClass='rounded-3xl py-6 sm:py-10 px-4 sm:px-6 bg-white w-full max-w-[95vw] sm:max-w-[28.8rem] md:w-[28.8rem] md:max-w-[30.8rem] lg:w-[28rem] mx-4 sm:mx-0 max-h-[95vh] flex flex-col overflow-hidden'
      hasCloseButton={true}
    >
      <section className='w-full flex flex-col flex-1 min-h-0'>
        <div className='text-center sm:text-left mb-6 -mt-2 sm:-mt-6 flex-shrink-0'>
          <h3 className='text-base sm:text-lg font-bold text-gray-900 mb-2'>
            {editingItem ? 'Edit Sub-category' : 'Add New Sub-category'}
          </h3>
          <p className='text-xs sm:text-sm text-gray-500 leading-relaxed'>
            {editingItem
              ? `Update the sub-category that vendors can use when creating their menus.`
              : `Add a new sub-category that vendors can use when creating their menus.`}
          </p>
        </div>

        <form
          onSubmit={(e) => e.preventDefault()}
          className='w-full flex flex-col flex-1 min-h-0'
        >
          <div className='space-y-4 flex-1 overflow-y-auto pr-2 custom-scroll'>
            <div>
              <label
                htmlFor='business_type_id'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Business type
              </label>
              <Select
                value={
                  values.business_type_id
                    ? String(values.business_type_id)
                    : undefined
                }
                onValueChange={(value) => {
                  setFieldValue('business_type_id', Number(value))
                  setFieldTouched('business_type_id', true)
                }}
                disabled={!!editingItem || !!preselectedBusinessTypeId || loading}
              >
                <SelectTrigger
                  className={cn(
                    'w-full rounded-[1rem]',
                    touched.business_type_id &&
                      errors.business_type_id &&
                      'border-red-500 focus:border-red-500'
                  )}
                >
                  <SelectValue
                    placeholder={
                      editingItem
                        ? editingItem.business_type_name || 'Select business type'
                        : 'Select business type'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((bt: any) => (
                    <SelectItem key={bt.id} value={String(bt.id)}>
                      {bt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {touched.business_type_id && errors.business_type_id && (
                <p className='text-xs text-red-500 mt-1'>
                  {errors.business_type_id as string}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor='name'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Name
              </label>
              <div className='w-full'>
                <Input
                  id='name'
                  name='name'
                  type='text'
                  placeholder='e.g rice'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={() => setFieldTouched('name', true)}
                  className={cn(
                    'w-full rounded-[1rem]',
                    touched.name &&
                      errors.name &&
                      'border-red-500 focus:border-red-500'
                  )}
                  error={errors.name}
                  touched={touched.name}
                />
              </div>
            </div>
            
            <div>
              <label
                htmlFor='image'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Upload Sub-category Image<span className='text-red-500'>*</span>
              </label>
              <div className='w-full mb-2'>
                {imagePreview ? (
                  <div className='relative w-full'>
                    <div className='relative w-full h-48 rounded-lg overflow-hidden border border-gray-200 bg-gray-50'>
                      <img
                        src={imagePreview}
                        alt='Preview'
                        className='w-full h-full object-cover'
                      />
                    </div>
                    <label
                      htmlFor='image-upload'
                      className='absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer'
                    >
                      <div className='flex items-center gap-2 text-white'>
                        <Camera className='w-5 h-5' />
                        <span className='text-sm font-medium'>Update image</span>
                      </div>
                    </label>
                    <input
                      ref={fileInputRef}
                      id='image-upload'
                      type='file'
                      className='hidden'
                      accept='image/jpeg,image/jpg,image/png'
                      onChange={handleImageChange}
                    />
                  </div>
                ) : (
                  <label
                    htmlFor='image-upload'
                    className='flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors'
                  >
                    <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                      <Camera className='w-8 h-8 mb-2 text-gray-400' />
                      <p className='mb-1 text-sm text-gray-500'>
                        <span className='font-semibold'>Upload category image</span>
                      </p>
                      <p className='text-xs text-gray-500'>
                        Allowed formats .jpg & .png. less than 1mb
                      </p>
                    </div>
                    <input
                      ref={fileInputRef}
                      id='image-upload'
                      type='file'
                      className='hidden'
                      accept='image/jpeg,image/jpg,image/png'
                      onChange={handleImageChange}
                    />
                  </label>
                )}
              </div>
              {touched.image && errors.image && (
                <p className='text-xs text-red-500 mt-1'>
                  {errors.image as string}
                </p>
              )}
            </div>
          </div>

          <div className='mt-6 sm:mt-8 flex flex-col sm:flex-row justify-center sm:justify-center gap-3 sm:space-x-3 flex-shrink-0 pt-4 border-t border-gray-100'>
            <Button
              type='button'
              variant='ghost'
              onClick={handleCloseModal}
              className='px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-normal w-full sm:w-auto order-2 sm:order-1'
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={() => {
                setFieldTouched('name', true)
                setFieldTouched('business_type_id', true)
                if (!editingItem) {
                  setFieldTouched('image', true)
                }
                if (isValid) {
                  const imageToSubmit =
                    selectedImage || selectedImageRef.current || values.image
                  const submitData = {
                    ...values,
                    image: imageToSubmit,
                    position: values.position ?? undefined,
                    active: values.active ?? true,
                    is_published: values.is_published ?? false,
                  }
                  setHasSubmitted(true)
                  onSubmit?.(submitData)
                }
              }}
              disabled={!isValid || loading}
              loadingText={editingItem ? 'Updating Sub-category...' : 'Adding Sub-category...'}
              loading={loading}
              className='px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl font-normal bg-primary hover:bg-primary/90 text-primary-foreground w-full order-1 sm:order-2'
            >
              {editingItem ? 'Update Sub-category' : 'Add Sub-category'}
            </Button>
          </div>
        </form>
      </section>
    </ModalContainer>
  )
}

export default AddCategoryModal
export { AddCategoryModal }
